import { ColDef, ColumnState, DateFilterModel, TextFilterModel, SetFilterModel } from 'ag-grid-community';
import { Component, Input, OnInit } from '@angular/core';
import { defaultRecurringTabColumnDefs } from '../transactions-table/transaction-table-definitions';
import { amountColumnTemplate, currencyComparator, dateComparator, dateComparatorForFilter, defaultColumnTemplate, getDisplayFrequency, mapTransactionType, sharedColNames } from '../transactions-table/table-helpers';
import { ActionsLinkComponent } from '../../../shared/actions-link/actions-link.component';
import { BehaviorSubject, combineLatest, filter, map, Observable, Subject, takeUntil, withLatestFrom } from 'rxjs';
import { TransactionsTableColumn } from '../../../shared/types/TransactionTableColumn';
import { select, Store } from '@ngrx/store';
import { selectHasNoRecurringTransactions, selectIsDemoUser, selectIsEmployee, selectIsPtrs, selectRecurringTransactions, selectSubAccountId } from '../../../account-info-header/reducers';
import { RecurringTransaction } from '../../models';
import {Constants, CreateTypes, transactionDirection, transactionTypes} from '../../../shared/utilities/Constants';
import { AchActions, CheckActions, InternalTransferActions, PtrsActions, TransactionsTableActions, WireActions } from '../../actions';
import { NamePipe } from '../../../shared/pipes/NamePipe';
import { selectedRowData, TableComponentState } from '../../../shared/types/TableComponentState';
import { selectCurrentTransaction, selectEditStartedTable, selectPtrsCallFinished, selectTransactionsTableState } from '../../reducers';
import { displayCancelModal } from '../../../shared/actions/shared.actions';
import { StandingAuthAchActions } from '../../../standing-authorizations/actions';
import { ToastrService } from 'ngx-toastr';
import { selectInquiryCallComplete, selectIsEauthForEditFlow } from '../../../transaction-view/reducers';
import { SharedActions } from '../../../shared/actions';
import { ViewPageActions } from '../../../transaction-view/actions';
import { editInProgress } from '../../actions/transactions-table-actions';
import { agFilterNames } from '../../../shared/types/AgFilterNames';
import { selectRecurringTable } from '../../../user-preference/reducers';
import { selectCreateTypeInContext } from '../../../shared/reducers';

@Component({
  standalone: false,
  selector: 'mm-recurring-transactions-table',
  templateUrl: './recurring-transactions-table.component.html',
  styleUrls: ['./recurring-transactions-table.component.scss']
})
export class RecurringTransactionsTableComponent implements OnInit{

  @Input() showCancelModalOnClick: boolean;

  private cancelLinkClicked$ = new BehaviorSubject<boolean>(false);
  cancelLinkClickedAsObservable$ = this.cancelLinkClicked$.asObservable();

  private currentSelectedTransaction$ = new BehaviorSubject<string | null>(null);
  currentSelectedTransactionAsObservable$ = this.currentSelectedTransaction$.asObservable();

  private deleteClicked$ = new BehaviorSubject<boolean>(false);
  deleteLinkClickedAsObservable$ = this.deleteClicked$.asObservable();

  rowData$: Observable<TransactionsTableColumn[]>;
  hasNoRecurringTransactions: boolean;
  columnDefs: ColDef[] = [];
  columnState: ColumnState[] | undefined;
  destroy$ = new Subject<void>();
  noExportCol: string[] = ['Actions'];
  fileName: string = 'recurring-all-';
  transactionsTableState$: Observable<TableComponentState>;
  currentActionLinkClickedEvent: any;
  isPtrs: boolean;
  subAccountId: string;
  newEditEvent: any;
  isDemoUser: boolean;
  isEmployee: boolean;
  colNames = {
    date: 'Transaction date',
    nextTransactionDate: 'Next transaction date',
    frequency: 'Frequency',
    amount: 'Amount',
    toFromAccount: sharedColNames.toFromAccount,
    transactionType: sharedColNames.transactionType,
    actions: sharedColNames.actions,
    status: 'Status',
    nickname: 'Nickname',
    recipientName: 'Recipient name',
    bankName: 'Bank name',
    accountNumber: 'Account number',
    dateCreated: 'Date created',
    IncomingOutgoing: 'Direction'
  }

  transactionActionsColumnDef =   {
    field: this.colNames.actions,
    filter: false,
    suppressMovable: true,
    sortable: false,
    cellRenderer: ActionsLinkComponent,
    cellRendererParams: {
      onClick: this.actionLinkClicked.bind(this),
      viewStatus: 'View',
      edit: 'Edit',
      cancel: 'Cancel',
      deactivateProfile: 'Deactivate'
    },
    minWidth: 220,
    maxWidth: 220
  };
  defaultColumnDefinitions: ColDef[] = defaultRecurringTabColumnDefs.concat(this.transactionActionsColumnDef)

  incomingOutgoingColumnObj = {
    aggFunc: null,
    colId: "Direction",
    floatingFilterComponent: 'customSetFloatingFilter',
    floatingFilterComponentParams: {
      suppressFilterButton: true,
    },
    flex: 1,
    hide: false,
    pinned: null,
    width: 120,
    pivot: false,
    pivotIndex: null,
    rowGroup: false,
    RowGroupindex: null,
    sort: null,
    sortIndex: null
  }

  nextTransactionColumnObj = {
    aggFunc: null,
    colId: "Next transaction date",
    floatingFilterComponent: 'customSetFloatingFilter',
    floatingFilterComponentParams: {
      suppressFilterButton: true,
    },
    flex: 1,
    hide: false,
    pinned: null,
    minWidth: 120,
    maxWidth: 120,
    pivot: false,
    pivotIndex: null,
    rowGroup: false,
    RowGroupindex: null,
    sort: null,
    sortIndex: null
  }

  filterModel: {
    [key: string]: TextFilterModel | DateFilterModel | SetFilterModel;
  };
  createTypeInContext: CreateTypes;
  recurringTransactions: any;

  constructor(
     private store: Store,
     private namePipe: NamePipe,
     private toastr: ToastrService,
  ){}

  ngOnInit(): void {

    this.setColumnPreferences();

    this.store.select(selectHasNoRecurringTransactions).pipe(takeUntil(this.destroy$))
    .subscribe(hasNoTransactions => this.hasNoRecurringTransactions = hasNoTransactions);

    this.transactionsTableState$ = this.store.select(selectTransactionsTableState);

    this.rowData$ = this.store
      .select(selectRecurringTransactions)
      .pipe(map((transactions) => this.mapRecurringTransactionsToRowData(transactions)));
    if (this.hasNoRecurringTransactions) {
      this.store.dispatch(TransactionsTableActions.hasEmptyResponse());
    }

    this.store.select(selectIsPtrs).pipe(
      takeUntil(this.destroy$)
    ).subscribe(isPtrs => this.isPtrs = isPtrs);

    this.store.select(selectSubAccountId).pipe(takeUntil(this.destroy$)).subscribe(subAccountId => { this.subAccountId = subAccountId });

    this.store.select(selectIsDemoUser).subscribe(value => {
      this.isDemoUser = value;
    });

    this.store.select(selectIsEmployee).subscribe(value => {
      this.isEmployee = value;
    });

        this.store.select(selectCreateTypeInContext).pipe(takeUntil(this.destroy$)).subscribe(createType => {
          this.createTypeInContext = createType;
        });

    combineLatest([
      this.store.select(selectPtrsCallFinished),
      this.store.select(selectInquiryCallComplete),
    ])
      .pipe(
        takeUntil(this.destroy$),
        withLatestFrom(
          this.store.select(selectIsEauthForEditFlow),
          this.store.select(selectCurrentTransaction),
          this.store.select(selectEditStartedTable)
        ),
        filter(
          ([[_ptrsCallComplete, inquiryCallComplete], _isEauthForEditFlow, _currentTransaction, editStarted]) =>
            inquiryCallComplete && editStarted
        )
      )
      .subscribe(
        ([
          [isPtrsCallFinished, _inquiryCallComplete],
          isEauthForEditFlow,
          _currentTransaction,
        ]) => {
          if (isEauthForEditFlow === true) {
            this.store.dispatch(SharedActions.showEditingRestrictedModal());
            this.store.dispatch(ViewPageActions.resetIsEauthForEditFlow());
          } else if (isEauthForEditFlow === false) {
            //for PTRS accounts, don't call Edit until PTRS call finishes
            if ((this.isPtrs && isPtrsCallFinished) || !this.isPtrs) {
              this.store.dispatch(editInProgress());
              this.store.dispatch(ViewPageActions.resetIsEauthForEditFlow());
            }
          }
        }
      );
  }

  setColumnPreferences(){
    let hasIncomingOutgoing = false;
    let hasTransactionDate = false;
    this.store.select(selectRecurringTable).pipe(takeUntil(this.destroy$)).subscribe(table => {
      table?.columns.map(sobj => {
        if(sobj?.colId?.includes('Direction')) {
          hasIncomingOutgoing = true;
        }
        if(sobj?.colId?.includes('Transaction date')){
          hasTransactionDate = true;
        }
      });
      this.columnState = table?.columns;
      if(this.columnState && !hasIncomingOutgoing) {
        this.columnState = Object.assign([], this.columnState);
        this.columnState?.splice(1, 0, this.incomingOutgoingColumnObj);
      }
      if(this.columnState && hasTransactionDate){
        this.columnState = Object.assign([], this.columnState);
        this.columnState?.splice(4, 1, this.nextTransactionColumnObj);
      }
      if (this.columnState) {
        this.columnDefs = this.buildColumnPreferences(this.columnState);
      } else {
        this.columnDefs = this.defaultColumnDefinitions;
      }
    });
    
  }

  mapRecurringTransactionsToRowData(
    transactions: RecurringTransaction[]
  ): TransactionsTableColumn[] {
    return transactions.map((transaction) => {
      return this.mapRecurringTransactions(transaction);
    });
  }

  mapRecurringTransactions(transaction: RecurringTransaction): TransactionsTableColumn {
    return {
      ...this.mapTransactions(transaction, true),
      Frequency: transaction.frequencyCd ? getDisplayFrequency(transaction.frequencyCd, transaction.mediaTypeCd) : transaction.frequencyDescription
    };
  }

  mapTransactions(
    transaction: RecurringTransaction,
    isActiveRecurring: boolean
  ): TransactionsTableColumn {
    let mappedTransaction: TransactionsTableColumn;
    mappedTransaction = {
      Type: mapTransactionType(
        transaction.typeDescription,
        this.findTransactionDirection(transaction)
      ),
      'Direction': this.findTransactionDirection(transaction),
      'To/From': this.namePipe.transform(
        `${transaction.to1} ${transaction.to2} ${transaction.to3}`
      ),
      Amount: transaction.outgoingAmount
        ? (transaction.outgoingAmount as string)
        : (transaction.incomingAmount as string),
      Status: transaction.statusCodeDescription,
      CashieringRqstId: transaction.cashieringRqstId,
      CashieringRqstSecondId: transaction.cashieringRqstSecondId,
      'IsEditable': transaction.isEditable,
      'IsCancelable': transaction.isCancelable,
      'IsActiveRecurring': isActiveRecurring,
      isBundled: transaction.bundledSloaIn
    };

    if ( isActiveRecurring ) {
      mappedTransaction = {
        ...mappedTransaction,
        'Next transaction date': transaction.processDate.split('-').join('/'),
      };
    }
    else {
      mappedTransaction = {
        ...mappedTransaction,
        'Transaction date': transaction.processDate.split('-').join('/')
      }
    }
    return  mappedTransaction;
  }

  findTransactionDirection(transaction: RecurringTransaction){
    return transaction.incomingAmount === null || transaction.incomingAmount === undefined ? transactionDirection.out : transactionDirection.in
  }

  actionLinkClicked(event: any) {
    if (event.name === 'View') {
      this.statusClicked(event);
    } else if (event.name === 'Edit') {
      this.editClicked(event);
    } else if (event.name === 'Cancel') {
      this.cancelClicked(event);
    } else if (event.name === 'Deactivate') {
      this.deleteClicked(event);
    }
  }

  statusClicked(event: any) {
    if (this.showCancelModalOnClick) {
      this.currentActionLinkClickedEvent = event;
      this.store.dispatch(displayCancelModal({
        nextUrlState: '',
        title: 'Cancel this transaction in progress?',
        content: ['This will reset your transaction inputs. Are you sure you want to continue?'],
        greenButton: 'Cancel transaction',
        grayButton: 'Back',
        source: 'transactionTableViewStatus'
      }));
    } else {
      const rowData = event.rowData as TransactionsTableColumn;
        this.store.dispatch(TransactionsTableActions.setCurrentViewedTransaction({ transaction: rowData }));
        this.store.dispatch(TransactionsTableActions.loadViewPage({ viewType: 'transaction' }));
        if (this.isEditRecurringAch(rowData)) {
          this.store.dispatch(SharedActions.updatePreviousContext({createType: this.createTypeInContext}));
        }
        this.callTransactionInquiry(event, true);
    }
  }

  editClicked(event: any) {
    if (this.showCancelModalOnClick) {
      this.currentActionLinkClickedEvent = event;
      this.store.dispatch(displayCancelModal({
        nextUrlState: '',
        title: 'Cancel this transaction in progress?',
        content: ['This will reset your transaction inputs. Are you sure you want to continue?'],
        greenButton: 'Cancel transaction',
        grayButton: 'Back',
        source: 'transactionTableEdit'
      }));
    } else {
      this.newEditEvent = event;
      this.updateEditPage(true);
    }
  }

  cancelClicked(event: any) {
    if (this.isDemoUser) {
      this.toastr.error(Constants.toastrMessageDemoUser);
    }
    else if (this.isEmployee) {
      this.toastr.error(Constants.toastrMessageEmployee);
    }
    else {
      const rowData = event.rowData as TransactionsTableColumn;
      this.store.dispatch(TransactionsTableActions.setCurrentViewedTransaction({ transaction: rowData }));
      this.cancelLinkClicked$.next(true);
    }
  }

  deleteClicked(event: any) {
    if (this.isDemoUser) {
      this.toastr.error(Constants.toastrMessageDemoUser);
    }
    else if (this.isEmployee) {
      this.toastr.error(Constants.toastrMessageEmployee);
    }
    else {
      const rowData = event.rowData;
      this.currentSelectedTransaction$.next(rowData.Type[0] + '-' + rowData.CashieringRqstId + '-' + rowData.CashieringRqstSecondId)
      this.deleteClicked$.next(true);
    }
  }

  callTransactionInquiry(event: any, isFromView: boolean) {
    this.store.pipe(takeUntil(this.destroy$),
      select(selectRecurringTransactions),
      map((element: any) => element.find((transaction: any) => { return transaction.cashieringRqstId === event.rowData['CashieringRqstId'] })),
      filter(res => res !== undefined)
    ).subscribe((res) => {
      this.recurringTransactions = res;
    });
    if (this.recurringTransactions.typeDescription?.includes('Journal')) {
      this.store.dispatch(InternalTransferActions.callInquiry({
        payload: this.recurringTransactions,
        viewType: 'transaction',
        transactionType: transactionTypes.internalTransfer
      }));
      if (this.isPtrs) {
        this.store.dispatch(PtrsActions.callPtrs({ accountId: this.subAccountId }));
      }
    } else if (this.recurringTransactions.typeDescription?.includes('MoneyLink')) {
      if (this.recurringTransactions.frequency !== " " && this.recurringTransactions.frequency !== "On-Request" && event.name === 'Edit') {
        let rowData: selectedRowData = {
          transactionType: transactionTypes.ach,
          transferDirection: (this.recurringTransactions.incomingAmount != null) ? 'incoming' : 'outgoing',
          standingAuthId: `a-${this.recurringTransactions.cashieringRqstId}-${this.recurringTransactions.cashieringRqstSecondId}`,
          frequency: 'recurring'
        };
        this.store.dispatch(
          TransactionsTableActions.editProfileClicked({ rowData })
        );
        this.store.dispatch(StandingAuthAchActions.callAchInquiry({
          payload: this.recurringTransactions,
          viewType: 'transaction',
          transactionType: transactionTypes.ach
        }));
        if (this.isPtrs) {
          this.store.dispatch(PtrsActions.callPtrs({ accountId: this.subAccountId }));
        }
      } else {
        this.store.dispatch(AchActions.callInquiry({
          payload: this.recurringTransactions,
          viewType: 'transaction',
          transactionType: transactionTypes.ach
        }))
      }
    } else if (this.recurringTransactions.typeDescription?.includes('Wire')) {
      this.store.dispatch(WireActions.callInquiry({
        payload: this.recurringTransactions,
        viewType: 'transaction',
        transactionType: transactionTypes.wire,
        resourceVersion: '3'
      }));
    } else if (this.recurringTransactions.typeDescription?.includes('Check')) {
      this.store.dispatch(CheckActions.callInquiry({
        payload: this.recurringTransactions,
        viewType: isFromView ? 'transaction' : '',
        transactionType: transactionTypes.check
      }));
    }
  }

  updateEditPage(accepted: boolean) {
    if (accepted) {
      this.store.dispatch(TransactionsTableActions.editTransactionClicked());
      const rowData = this.newEditEvent.rowData;
      if (this.isEditRecurringAch(rowData)) {
        this.store.dispatch(SharedActions.updatePreviousContext({createType: this.createTypeInContext}));
      }
      this.store.dispatch(TransactionsTableActions.setCurrentViewedTransaction({ transaction: rowData }));
      this.callTransactionInquiry(this.newEditEvent, false);
    }
  };

  private isEditRecurringAch(rowData: any): boolean {
    let frequency: string = rowData.Frequency;
    let type: string = rowData.Type;

    if (!type.includes('ACH')) {
      return false;
    }
    if (frequency.length === 0 || frequency === 'One-time') {
      return false
    }
    return true;
  }

  buildColumnPreferences(preferences: ColumnState[], isSavedTemplateTab: boolean = false): ColDef[] {
    // Remove previously existing columns that will now appear empty and prevent new bad columns
    let updatedPreferences: ColumnState[] = [];
    var hasDirection = false;
    preferences.forEach(preference => {
      if(
      !preference.colId?.includes('Funds direction') &&
      !preference.colId?.includes('Incoming\/Outgoing') &&
      isNaN(Number(preference.colId))){
        updatedPreferences.push(preference);
      }
      if (preference.colId?.includes('Direction')) {
        hasDirection = true;
      }
    });

    if (!hasDirection && !isSavedTemplateTab) {
      updatedPreferences.splice(1, 0, this.incomingOutgoingColumnObj);
    }

    let result: ColDef[] = updatedPreferences.map((preference) => {
      switch (preference.colId) {
        case 'Type':
          return {
            field: this.colNames.transactionType,
            filter: agFilterNames.set,
            sort: preference.sort,
            floatingFilterComponent: 'customSetFloatingFilter',
            floatingFilterComponentParams: {
              suppressFilterButton: true,
            },
            headerComponentParams: { template: defaultColumnTemplate },
            flex: 1,
            minWidth: 300,
            headerClass: params => params.column?.getSort() ? 'sorted-header-bold' : ''
          }
        case 'Direction':
          return {
            field: this.colNames.IncomingOutgoing,
            sort: preference.sort,
            flex: 1,
            floatingFilterComponent: 'customSetFloatingFilter',
            floatingFilterComponentParams: {
              suppressFilterButton: true,
            },
            headerComponentParams: { template: defaultColumnTemplate },
            filter: agFilterNames.set,
            minWidth: 120,
            hide: false,
            pinned: null,
            pivot: false,
            pivotIndex: null,
            rowGroup: false,
            rowGroupIndex: null,
            sortIndex: null,
            aggFunc: null,
            headerClass: params => params.column?.getSort() ? 'sorted-header-bold' : ''
          }
        case 'To/From':
          return {
            field: this.colNames.toFromAccount,
            sort: preference.sort,
            floatingFilterComponent: 'customTextFloatingFilter',
            floatingFilterComponentParams: {
              suppressFilterButton: true,
            },
            headerComponentParams: { template: defaultColumnTemplate },
            filterParams: {
              textFormatter: (value: string) => value == null ? null : value.toLowerCase().replaceAll('-', '')
            },
            flex: 1,
            minWidth: 360,
            headerClass: params => params.column?.getSort() ? 'sorted-header-bold' : ''
          }
        case 'Amount':
          return {
            field: this.colNames.amount,
            comparator: currencyComparator,
            sort: preference.sort,
            floatingFilterComponent: 'customTextFloatingFilter',
            floatingFilterComponentParams: {
              suppressFilterButton: true,
            },
            filterParams: {
              textFormatter: (value: string) => value == null ? null : value.toLowerCase().replaceAll(',', '')
            },
            type: 'rightAligned',
            headerComponentParams: { template: amountColumnTemplate },
            minWidth: 120,
            width: 120,
            maxWidth: 140,
            headerClass: params => params.column?.getSort() ? 'sorted-header-bold' : ''
          }
        case 'Transaction date':
          return {
            field: this.colNames.date,
            comparator: dateComparator,
            filter: agFilterNames.date,
            sort: preference.sort,
            filterParams: {
              comparator: dateComparatorForFilter,
              inRangeInclusive: true,
              browserDatePicker: true
            },
            headerComponentParams: { template: defaultColumnTemplate },
            minWidth: 160,
            maxWidth: 160,
            headerClass: params => params.column?.getSort() ? 'sorted-header-bold' : ''
          }

          case 'Next transaction date':
          return {
            field: this.colNames.nextTransactionDate,
            comparator: dateComparator,
            filter: agFilterNames.date,
            sort: preference.sort,
            filterParams: {
              comparator: dateComparatorForFilter,
              inRangeInclusive: true,
              browserDatePicker: true
            },
            headerComponentParams: { template: defaultColumnTemplate },
            minWidth: 170,
            maxWidth: 180,
            headerClass: params => params.column?.getSort() ? 'sorted-header-bold' : ''
          }
        case 'Frequency':
          return {
            field: this.colNames.frequency,
            filter: agFilterNames.set,
            sort: preference.sort,
            floatingFilterComponent: 'customSetFloatingFilter',
            floatingFilterComponentParams: {
              suppressFilterButton: true,
            },
            headerComponentParams: { template: defaultColumnTemplate },
            width: 120,
            minWidth: 120,
            maxWidth: 130,
            headerClass: params => params.column?.getSort() ? 'sorted-header-bold' : ''
          }
        case 'Actions':
          return {
              field: this.colNames.actions,
              filter: false,
              suppressMovable: true,
              sortable: false,
              cellRenderer: ActionsLinkComponent,
              cellRendererParams: {
                onClick: this.actionLinkClicked.bind(this),
                viewStatus: 'View',
                edit: 'Edit',
                cancel: 'Cancel',
                deactivateProfile: 'Deactivate',
              },
              minWidth: 220,
              maxWidth: 220
            };
        case 'Status':
          return {
            field: this.colNames.status,
            filter: agFilterNames.set,
            sort: preference.sort,
            floatingFilterComponent: 'customSetFloatingFilter',
            floatingFilterComponentParams: {
              suppressFilterButton: true,
            },
            headerComponentParams: { template: defaultColumnTemplate },
            width: 120,
            minWidth: 120,
            maxWidth: 130,
            headerClass: params => params.column?.getSort() ? 'sorted-header-bold' : ''
          }
        case 'Bank name':
          return {
            field: this.colNames.bankName,
            sort: preference.sort,
            floatingFilterComponent: 'customTextFloatingFilter',
            floatingFilterComponentParams: {
              suppressFilterButton: true,
            },
            headerComponentParams: { template: defaultColumnTemplate },
            width: 180,
            minWidth: 120,
            maxWidth: 210,
            headerClass: params => params.column?.getSort() ? 'sorted-header-bold' : ''
          }

        case 'Account number':
          return {
            field: this.colNames.accountNumber,
            sort: preference.sort,
            floatingFilterComponent: 'customTextFloatingFilter',
            floatingFilterComponentParams: {
              suppressFilterButton: true,
            },
            headerComponentParams: { template: defaultColumnTemplate },
            minWidth: 140,
            maxWidth: 140,
            headerClass: params => params.column?.getSort() ? 'sorted-header-bold' : ''
          }

        case 'Date created':
          return {
            field: this.colNames.dateCreated,
            sort: preference.sort,
            comparator: dateComparator,
            filter: agFilterNames.date,
            filterParams: {
              comparator: dateComparatorForFilter,
              inRangeInclusive: true,
              browserDatePicker: true
            },
            headerComponentParams: { template: defaultColumnTemplate },
            minWidth: 160,
            maxWidth: 160,
            headerClass: params => params.column?.getSort() ? 'sorted-header-bold' : ''
          }
        default:
          return {}
      }
    })
    return result;
  }
}
